import React from "react";

import { css } from "@compiled/react";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives/responsive";

import type { spaceTokenNumberValue } from "@/libraries/design/utils";

import { ListItemNumber } from "./list-item-number.tsx";

export type ListAs = "ul" | "ol" | "dl";
type ViewportSize = "mobile" | "tablet" | "desktop";
type ResponsiveGapOptions = Record<ViewportSize, spaceTokenNumberValue>;

type UniformContainerOrientation = "horizontal" | "vertical";

type ResponsiveOrientationOptions = Record<
    ViewportSize,
    UniformContainerOrientation
>;

type WrappedChildContentOptions = Record<
    ViewportSize,
    "nowrap" | "wrap" | "wrap-reverse"
>;

type ListTypeComponentJustifyContentOptions = Record<
    ViewportSize,
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly"
>;

export interface UniformListV2Props {
    listType: ListAs;
    gapv2?: ResponsiveGapOptions;
    orientation?: ResponsiveOrientationOptions;
    wrappedChildContent?: WrappedChildContentOptions;
    justifyContent?: ListTypeComponentJustifyContentOptions;
}

// Helper functions to reduce complexity
const createResponsiveGapObject = (
    gapv2: Partial<ResponsiveGapOptions> & { mobile?: spaceTokenNumberValue }
): ResponsiveGapOptions => ({
    mobile: gapv2.mobile ?? "0",
    tablet: gapv2?.tablet ?? gapv2.mobile ?? "0",
    desktop: gapv2?.desktop ?? gapv2?.tablet ?? gapv2.mobile ?? "0"
});

const createResponsiveOrientationObject = (
    orientation: Partial<ResponsiveOrientationOptions> & {
        mobile?: UniformContainerOrientation;
    }
): ResponsiveOrientationOptions => ({
    mobile: orientation.mobile || "vertical",
    tablet: orientation.tablet || orientation.mobile || "vertical",
    desktop:
        orientation.desktop ||
        orientation.tablet ||
        orientation.mobile ||
        "vertical"
});

const createResponsiveWrappedChildContentObject = (
    wrappedChildContent: Partial<WrappedChildContentOptions> & {
        mobile?: "nowrap" | "wrap" | "wrap-reverse";
    }
): WrappedChildContentOptions => ({
    mobile: wrappedChildContent?.mobile || "nowrap",
    tablet:
        wrappedChildContent?.tablet || wrappedChildContent?.mobile || "nowrap",
    desktop:
        wrappedChildContent?.desktop ||
        wrappedChildContent?.tablet ||
        wrappedChildContent?.mobile ||
        "nowrap"
});

const createResponsiveJustifyContentObject = (
    justifyContent: Partial<ListTypeComponentJustifyContentOptions> & {
        mobile?:
            | "flex-start"
            | "flex-end"
            | "center"
            | "space-between"
            | "space-around"
            | "space-evenly";
    }
): ListTypeComponentJustifyContentOptions => ({
    mobile: justifyContent.mobile || "flex-start",
    tablet: justifyContent?.tablet || justifyContent?.mobile || "flex-start",
    desktop:
        justifyContent?.desktop ||
        justifyContent?.tablet ||
        justifyContent.mobile ||
        "flex-start"
});

const getFlexWrapValue = (
    value: "nowrap" | "wrap" | "wrap-reverse"
): "wrap" | "wrap-reverse" | "nowrap" => {
    if (value === "wrap") return "wrap";
    if (value === "wrap-reverse") return "wrap-reverse";
    return "nowrap";
};

const zeroMarginPadding = css({
    margin: 0,
    padding: 0
});

const resetListStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& ul"]: {
        listStyleType: "disc",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["& ul"]: {
            listStyleType: "circle",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["& ul"]: {
                listStyleType: "square"
            }
        }
    }
});

const createRenderListItems = (
    listType: ListAs
): React.ComponentType<UniformSlotWrapperComponentProps> => {
    return ({ items }) => {
        // Only works for paired dt/dd items. Would need different solution for different structure
        if (listType === "dl") {
            return items.map((item, index) => {
                if (index % 2 === 0) {
                    return (
                        <React.Fragment key={index}>
                            <dt>{item}</dt>
                            {items[index + 1] && <dd>{items[index + 1]}</dd>}
                        </React.Fragment>
                    );
                }
                return null;
            });
        }

        if (listType === "ol") {
            return items.map((item, index) => (
                <li
                    key={index}
                    css={[
                        zeroMarginPadding,
                        css({
                            listStyle: "none",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            [media.above.sm]: {
                                flexDirection: "row",
                                alignItems: "baseline"
                            }
                        })
                    ]}
                >
                    <ListItemNumber value={index + 1} />
                    {item}
                </li>
            ));
        }

        return items.map((item, index) => (
            <li
                key={index}
                css={[
                    css({ all: "unset", display: "flex" }),
                    zeroMarginPadding
                ]}
            >
                {item}
            </li>
        ));
    };
};

const UniformListV2: React.FC<UniformListV2Props> = ({
    listType,
    gapv2 = {
        mobile: "0"
    },
    orientation = {
        mobile: "vertical"
    },
    wrappedChildContent = {
        mobile: "nowrap"
    },
    justifyContent = {
        mobile: "flex-start"
    }
}) => {
    const gapObject = createResponsiveGapObject(gapv2);
    const orientationObject = createResponsiveOrientationObject(orientation);
    const wrappedChildContentObject =
        createResponsiveWrappedChildContentObject(wrappedChildContent);
    const justifyContentObject =
        createResponsiveJustifyContentObject(justifyContent);

    // css() calls must be inline for Compiled CSS static analysis
    const listStyle = css({
        display: "flex",
        flexDirection: "column",
        gap: "var(--ds-space-" + gapObject.mobile + ")",
        [media.above.sm]: {
            gap: "var(--ds-space-" + gapObject.tablet + ")"
        },
        [media.above.md]: {
            gap: "var(--ds-space-" + gapObject.desktop + ")"
        }
    });

    const orientationStyle = css({
        display: "flex",
        flexDirection:
            orientationObject.mobile === "horizontal" ? "row" : "column",
        [media.above.sm]: {
            flexDirection:
                orientationObject.tablet === "horizontal" ? "row" : "column"
        },
        [media.above.md]: {
            flexDirection:
                orientationObject.desktop === "horizontal" ? "row" : "column"
        }
    });

    const wrappedChildContentStyle = css({
        display: "flex",
        flexWrap: getFlexWrapValue(wrappedChildContentObject.mobile),
        [media.above.sm]: {
            flexWrap: getFlexWrapValue(wrappedChildContentObject.tablet)
        },
        [media.above.md]: {
            flexWrap: getFlexWrapValue(wrappedChildContentObject.desktop)
        }
    });

    const justifyContentStyle = css({
        justifyContent: justifyContentObject.mobile,
        [media.above.sm]: {
            justifyContent: justifyContentObject.tablet
        },
        [media.above.md]: {
            justifyContent: justifyContentObject.desktop
        }
    });

    const renderListItems = createRenderListItems(listType);
    const ListTypeComponent = listType || "ul";

    return (
        <ListTypeComponent
            css={[
                listStyle,
                zeroMarginPadding,
                resetListStyles,
                orientationStyle,
                wrappedChildContentStyle,
                justifyContentStyle
            ]}
        >
            <UniformSlot name="listItems" wrapperComponent={renderListItems} />
        </ListTypeComponent>
    );
};

export default UniformListV2;
